import { logger } from 'packages';
import api from './api';
import utils from '../checkout/utils';
import { setFetching, setCartData, setError } from './reducer';
import * as analytics from 'tradera-apps/tradera/components/vip/helpers/analytics';
import cookieUtil from 'tradera-utils/cookie';
import { CART_ID } from 'tradera-constants/cookies';
import { selectShippingCountry } from 'tradera-state/shipping-region/selectors';

export const fetchCartData = cartId => async (dispatch, getState) => {
    dispatch(setFetching(true));

    try {
        const { countryCodeIso2 } = selectShippingCountry(getState());
        const checkoutData = await api.getCartOverview(cartId, countryCodeIso2);

        const { data } = checkoutData;

        dispatch(setCartData(data));
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const addItemToCart = (
    cartId,
    itemId,
    price,
    onSuccess
) => async dispatch => {
    dispatch(setFetching(true));

    try {
        const addItemResult = await api.addItemToCart(cartId, itemId, price);
        if (addItemResult.status === 200) {
            cartId = addItemResult.data.cartId;
            let date = new Date();
            date.setMonth(date.getMonth() + 2);
            cookieUtil.createCookie(CART_ID, cartId, date);
        }
        await api.updateCartSummary(cartId);
        onSuccess();
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const removeItemsFromCart = (cartId, itemIds) => async dispatch => {
    dispatch(setFetching(true));

    try {
        await api.removeItemsFromCart(cartId, itemIds);
        await api.updateCartSummary(cartId);
        dispatch(fetchCartData(cartId));
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const setCartItemQuantity = (
    cartId,
    itemId,
    quantity
) => async dispatch => {
    dispatch(setFetching(true));

    try {
        await api.setCartItemQuantity(cartId, itemId, quantity);
        await api.updateCartSummary(cartId);
        dispatch(fetchCartData(cartId));
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const confirmCartPurchase = (
    cartId,
    preorderId,
    shopItems,
    onSuccess
) => async (dispatch, getState) => {
    dispatch(setFetching(true));

    try {
        const confirmData = await api.confirmCartPurchase(
            shopItems,
            analytics.getGaTrackingData()
        );

        const { countryCodeIso2 } = selectShippingCountry(getState());
        await removeFromCartWithoutReload(
            cartId,
            shopItems.map(item => item.itemId),
            countryCodeIso2
        );

        onSuccess(confirmData.data.transactionIds[0]);
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));

        const errorCode = error?.response?.data?.responseStatus?.errorCode;
        dispatch(setError({ id: preorderId, message: errorCode }));
    }
};

export const createUncommittedPurchaseOrder = (
    cartId,
    preorderId,
    sellerMemberId,
    shopItems,
    onSuccess
) => async dispatch => {
    dispatch(setFetching(true));
    const purchaseOrderId = utils.getUuid();

    try {
        await api.createUncommittedPurchaseOrder(
            purchaseOrderId,
            sellerMemberId,
            shopItems.flatMap(item =>
                Array(item.quantity).fill({
                    itemId: item.itemId,
                    itemPrice: item.expectedItemPrice
                })
            )
        );

        onSuccess(purchaseOrderId);
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));

        const errorCode = error?.response?.data?.responseStatus?.errorCode;
        dispatch(setError({ id: preorderId, message: errorCode }));
    }
};

export const startOffPlatformCheckout = (
    itemId,
    onSuccess
) => async dispatch => {
    dispatch(setFetching(true));

    try {
        await api.startOffPlatformCheckout(itemId);
        onSuccess();
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const removeFromCartWithoutReload = async (
    cartId,
    itemIds,
    countryCodeIso2
) => {
    if (!cartId) return;
    await api.removeItemsFromCart(cartId, itemIds);
    await api.updateCartSummary(cartId);

    const { data } = await api.getCartOverview(cartId, countryCodeIso2);
    setCartData(data);
};
